const FROZEN_PANE_COLUMN_ATTRIBUTE = 'data-xlsx-frozen-pane-column';
const FROZEN_PANE_COLUMN_BOUNDARY_ATTRIBUTE = 'data-xlsx-frozen-pane-column-boundary';
const FROZEN_PANE_ROW_ATTRIBUTE = 'data-xlsx-frozen-pane-row';
const FROZEN_PANE_ROW_BOUNDARY_ATTRIBUTE = 'data-xlsx-frozen-pane-row-boundary';
const FROZEN_PANE_ATTRIBUTES = [
  FROZEN_PANE_COLUMN_ATTRIBUTE,
  FROZEN_PANE_COLUMN_BOUNDARY_ATTRIBUTE,
  FROZEN_PANE_ROW_ATTRIBUTE,
  FROZEN_PANE_ROW_BOUNDARY_ATTRIBUTE,
] as const;
const FROZEN_PANE_STYLE_PROPERTIES = [
  '--xlsx-frozen-pane-background-color',
  '--xlsx-frozen-pane-column-offset',
  '--xlsx-frozen-pane-row-top',
] as const;

interface ApplySpreadsheetFreezePaneOptions {
  activeContent: HTMLDivElement;
  columnCellKey?: string;
  rowCellKey?: string;
}

const getCellByKey = (root: HTMLElement | null, cellKey: string | undefined) => (
  cellKey
    ? root?.querySelector<HTMLElement>(`#${CSS.escape(`CELL-${cellKey}`)}`) ?? null
    : null
);

const getLogicalRowIndex = (row: HTMLTableRowElement): string | undefined => (
  row.querySelector<HTMLElement>('td[data-row-index]')?.dataset.rowIndex
);

const clearSpreadsheetFreezePane = (activeContent: HTMLDivElement) => {
  activeContent.removeAttribute('data-xlsx-custom-freeze-pane');
  activeContent
    .querySelectorAll<HTMLElement>(FROZEN_PANE_ATTRIBUTES.map(attribute => `[${attribute}]`).join(','))
    .forEach(cell => {
      FROZEN_PANE_ATTRIBUTES.forEach(attribute => cell.removeAttribute(attribute));
      FROZEN_PANE_STYLE_PROPERTIES.forEach(property => cell.style.removeProperty(property));
    });
};

const markFrozenPaneCell = (cell: HTMLElement, attribute: string) => {
  const ownerWindow = cell.ownerDocument.defaultView ?? window;
  cell.setAttribute(attribute, 'true');
  cell.style.setProperty('--xlsx-frozen-pane-background-color', ownerWindow.getComputedStyle(cell).backgroundColor);
};

export const applySpreadsheetFreezePane = ({
  activeContent,
  columnCellKey,
  rowCellKey,
}: ApplySpreadsheetFreezePaneOptions) => {
  clearSpreadsheetFreezePane(activeContent);

  const contentColumns = activeContent.querySelector<HTMLElement>('[id="spreadsheet-table__content-columns"]');
  if (!contentColumns || (!columnCellKey && !rowCellKey)) {
    return () => clearSpreadsheetFreezePane(activeContent);
  }

  activeContent.setAttribute('data-xlsx-custom-freeze-pane', 'true');
  const titleColumnsContainer = activeContent.querySelector<HTMLElement>('.titles-columns-container');

  if (rowCellKey) {
    const selectedCell =
      getCellByKey(contentColumns, rowCellKey)
      ?? getCellByKey(titleColumnsContainer, rowCellKey);
    const selectedRow = selectedCell?.closest('tr') as HTMLTableRowElement | null;
    const selectedLogicalRowIndex = selectedRow ? getLogicalRowIndex(selectedRow) : undefined;
    if (!selectedLogicalRowIndex) {
      return () => clearSpreadsheetFreezePane(activeContent);
    }

    const contentRows = Array.from(contentColumns.querySelectorAll<HTMLTableRowElement>('tr'))
      .filter((row): row is HTMLTableRowElement => (
        row.tagName === 'TR' && Boolean(row.querySelector('td[data-row-index]'))
      ));
    const selectedContentRowIndex = contentRows.findIndex(row => getLogicalRowIndex(row) === selectedLogicalRowIndex);
    if (selectedContentRowIndex === -1) {
      return () => clearSpreadsheetFreezePane(activeContent);
    }

    const rowsBeforeSelection = contentRows.slice(0, selectedContentRowIndex);
    const columnLegendHeight = contentColumns.querySelector<HTMLElement>('td.col-legend')?.offsetHeight ?? 0;
    let topOffset = columnLegendHeight;

    rowsBeforeSelection.forEach((row, rowIndex) => {
      const logicalRowIndex = row.querySelector<HTMLElement>('td[data-row-index]')?.dataset.rowIndex;
      const rowCells = Array.from(row.cells) as HTMLElement[];
      const titleRowCells = logicalRowIndex === undefined
        ? []
        : Array.from(
          activeContent.querySelectorAll<HTMLElement>(
            `.titles-columns-container td[data-row-index="${logicalRowIndex}"]`
          )
        );
      const frozenCells = [...rowCells, ...titleRowCells];

      frozenCells.forEach(cell => {
        markFrozenPaneCell(cell, FROZEN_PANE_ROW_ATTRIBUTE);
        cell.style.setProperty('--xlsx-frozen-pane-row-top', `${topOffset}px`);
        if (rowIndex === rowsBeforeSelection.length - 1) {
          cell.setAttribute(FROZEN_PANE_ROW_BOUNDARY_ATTRIBUTE, 'true');
        }
      });

      topOffset += row.offsetHeight;
    });
  }

  if (columnCellKey) {
    const selectedCell = getCellByKey(contentColumns, columnCellKey);
    const selectedColumnLegend = selectedCell?.dataset.columnLegend;
    if (!selectedColumnLegend) {
      return () => clearSpreadsheetFreezePane(activeContent);
    }

    const columnLegendCells = Array.from(
      contentColumns.querySelectorAll<HTMLElement>('td.col-legend[data-column-legend]')
    );
    const selectedColumnIndex = columnLegendCells.findIndex(
      columnCell => columnCell.dataset.columnLegend === selectedColumnLegend
    );
    if (selectedColumnIndex === -1) {
      return () => clearSpreadsheetFreezePane(activeContent);
    }

    const columnsBeforeSelection = columnLegendCells.slice(0, selectedColumnIndex);
    let leftOffset = 0;

    columnsBeforeSelection.forEach((columnCell, columnIndex) => {
      const { columnLegend } = columnCell.dataset;
      if (!columnLegend) {
        return;
      }

      const matchingColumnCells = Array.from(
        contentColumns.querySelectorAll<HTMLElement>(
          `td[data-column-legend="${CSS.escape(columnLegend)}"]`
        )
      );
      matchingColumnCells.forEach(cell => {
        markFrozenPaneCell(cell, FROZEN_PANE_COLUMN_ATTRIBUTE);
        cell.style.setProperty('--xlsx-frozen-pane-column-offset', `${leftOffset}px`);
        if (columnIndex === columnsBeforeSelection.length - 1) {
          cell.setAttribute(FROZEN_PANE_COLUMN_BOUNDARY_ATTRIBUTE, 'true');
        }
      });

      leftOffset += columnCell.offsetWidth;
    });
  }

  return () => clearSpreadsheetFreezePane(activeContent);
};
